import { WalkBookmark } from "./TravelCollections";
import { PublicGuideAd } from "../ads/GuideEndAd";
import { useEffect, useState } from "react";
import {
	contentFreshness,
	curatedWalks,
	editions,
	getWalk,
	googleMapsSearchUrl,
	placeCandidates,
	walkCoverage,
	type AirportCode,
	type CuratedWalk,
	type PlaceCandidate,
} from "@japangolmok/content";
import { appendTripStops, type TripWorkspace } from "@japangolmok/core";
import { listTrips } from "../storage/trips";
import { isAppsInTossChannel } from "../api-url";
import { track } from "../analytics/events";
import { ExternalLink, Layout, usePageMeta } from "../ui/Shell";
import { candidateToStop, tasteOptions } from "./TripExperience";
import { useTripWorkspace } from "./useTripWorkspace";

export function WalkPhoto({ walk }: { walk: CuratedWalk }) {
	const [failed, setFailed] = useState(false);
	return walk.photo && !failed ? (
		<figure className="walk-photo">
			<img
				src={walk.photo.publicPath}
				srcSet={`${walk.photo.publicPath.replace(".webp", "-480.webp")} 480w, ${walk.photo.publicPath.replace(".webp", "-960.webp")} 960w, ${walk.photo.publicPath} 1280w`}
				sizes="(max-width: 760px) calc(100vw - 32px), 50vw"
				loading="lazy"
				alt={walk.photo.subject}
				width="1280"
				height="960"
				decoding="async"
				onError={() => setFailed(true)}
			/>
			<figcaption>
				{walk.photoCaption} · {walk.photo.attributionText}{" "}
				<ExternalLink href={walk.photo.sourceUrl}>사진 출처</ExternalLink> ·{" "}
				<ExternalLink href={walk.photo.licenseUrl}>이용 조건</ExternalLink>
			</figcaption>
		</figure>
	) : (
		<div className="walk-photo-fallback" role="status">
			<p>사진을 불러오지 못했어요.</p>
			{walk.photo && (
				<button
					className="button secondary"
					type="button"
					onClick={() => setFailed(false)}
				>
					사진 다시 보기
				</button>
			)}
			<p>{walk.neighborhood}의 장소 안내는 아래에서 계속 볼 수 있어요.</p>
		</div>
	);
}
export function WalkListPage() {
	const query = new URLSearchParams(window.location.search);
	const initial = query.get("airport") ?? "";
	const [airport, setAirport] = useState(
		Object.hasOwn(editions, initial) ? initial : "",
	);
	const [taste, setTaste] = useState("");
	const [condition, setCondition] = useState(
		query.get("condition") === "rain"
			? "rain"
			: query.get("condition") === "tired"
				? "tired"
				: "normal",
	);
	const shown = curatedWalks.filter(
		(walk) =>
			(!airport || walk.airportCodes.includes(airport as AirportCode)) &&
			(!taste || walk.styles.includes(taste as never)) &&
			(condition === "normal" ||
				(walk.conditions ?? []).includes(condition as "rain" | "tired")),
	);
	usePageMeta(
		"동네의 반나절 — 일본골목",
		"산책·한 끼·쉼을 함께 골라요. 장소와 방문 조건을 확인하고 내 여행에 담아 현지에서 다시 꺼내요.",
	);
	useEffect(() => {
		track("walk_discovery_open", { surface: "walk_list" });
	}, []);
	return (
		<Layout>
			<section className="walk-intro paper-pattern">
				<a className="back-link" href="/">
					← 일본골목 홈
				</a>
				<h1>
					어떤 반나절을
					<br />
					보내고 싶으세요?
				</h1>
				<p>날짜 없이 골라 두고, 내 여행에 담아요.</p>
			</section>
			<section className="section" id="walk-results">
				<div className="walk-filters">
					<label>
						도시·공항권
						<select
							value={airport}
							onChange={(e) => setAirport(e.target.value)}
						>
							<option value="">전체 동네</option>
							{Object.values(editions).map((e) => (
								<option key={e.iata} value={e.iata}>
									{e.city} · {e.iata}
								</option>
							))}
						</select>
					</label>
					<label>
						더 보고 싶은 것
						<select value={taste} onChange={(e) => setTaste(e.target.value)}>
							<option value="">취향 상관없이</option>
							{tasteOptions.map((style) => (
								<option key={style.value} value={style.value}>
									{style.label}
								</option>
							))}
						</select>
					</label>
				</div>
				<div className="filter-chips" role="group" aria-label="오늘의 상황">
					{[
						["normal", "보통의 반나절"],
						["rain", "비를 줄이고 싶어요"],
						["tired", "천천히 걷고 싶어요"],
					].map(([value, label]) => (
						<button
							type="button"
							key={value}
							aria-pressed={condition === value}
							className={condition === value ? "is-active" : ""}
							onClick={() => setCondition(value!)}
						>
							{label}
						</button>
					))}
				</div>
				<p className="walk-filter-summary" role="status">
					{shown.length
						? "고른 조건의 편집 코스 " + shown.length + "개"
						: "편집 코스가 없는 조건은 아래에서 확인된 장소를 직접 골라요."}
					{condition === "rain"
						? " · 실내·지붕 있는 선택지 포함, 전체 이동이 실내라는 뜻은 아니에요."
						: ""}
				</p>
				{shown.length > 0 && (
					<div className="walk-list">
						{shown.map((walk) => (
							<article className="walk-edition walk-list-card" key={walk.slug}>
								<WalkPhoto walk={walk} />
								<div className="walk-edition-copy">
									<WalkBookmark slug={walk.slug} title={walk.title} />
									<span className="eyebrow">{walk.neighborhood}</span>
									<h2>
										<a
											href={
												"/walks/" +
												walk.slug +
												(condition !== "normal"
													? "?condition=" + condition
													: "")
											}
										>
											{walk.title}
										</a>
									</h2>
									<p>{walk.summary}</p>
									<p className="walk-time">
										체류 제안 {walk.stayMinutes}분 · 식사·이동 별도
									</p>
									<details>
										<summary>이 코스가 맞는 날과 걷기 조건</summary>
										<p>{walk.fit}</p>
										<p>다른 선택이 나을 때: {walk.notFit}</p>
									</details>
									<a
										className="button secondary"
										href={
											"/walks/" +
											walk.slug +
											(condition !== "normal" ? "?condition=" + condition : "")
										}
									>
										이 반나절 살펴보기 →
									</a>
								</div>
							</article>
						))}
					</div>
				)}
				{!shown.length && (
					<div className="empty-state">
						<h2>이 조건의 편집 코스는 아직 없어요.</h2>
						<p>
							공식 안내에 연결된 지역 후보는 아래에서 고를 수 있어요. 자료가
							없는 코스를 만들어 보여주지 않아요.
						</p>
						<button
							className="button secondary"
							type="button"
							onClick={() => {
								setAirport("");
								setTaste("");
								setCondition("normal");
							}}
						>
							전체 동네 보기
						</button>
					</div>
				)}
				{airport && (
					<AreaCandidates
						key={airport}
						airport={airport as AirportCode}
						taste={taste}
						condition={condition}
					/>
				)}
				<details className="workspace-disclosure">
					<summary>30개 공항권의 준비 범위와 출처</summary>
					<ul className="coverage-rows">
						{walkCoverage(new Date().toISOString()).map((row) => (
							<li key={row.airport}>
								<a href={"/airports/" + row.airport.toLowerCase()}>
									{editions[row.airport as AirportCode].city} · {row.airport}
								</a>
								<span>
									{row.status === "curated_walk"
										? "편집 코스 " + row.walkCount + "개"
										: "지역 후보 안내"}{" "}
									· {row.checkedAt ?? "확인일 미확인"}
								</span>
							</li>
						))}
					</ul>
				</details>
			</section>
		</Layout>
	);
}

function AreaCandidates({
	airport,
	taste,
	condition,
}: {
	airport: AirportCode;
	taste: string;
	condition: string;
}) {
	const [selected, setSelected] = useState<string[]>([]);
	const all = placeCandidates(airport);
	const places = all.filter(
		(p) =>
			(!taste || p.styles.includes(taste as never)) &&
			(condition !== "rain" || ["indoor", "covered"].includes(p.setting)),
	);
	const shown = places.length ? places : all;
	return (
		<section className="area-candidates">
			<div className="section-heading">
				<div>
					<span className="eyebrow">
						{editions[airport].city}의 확인된 후보
					</span>
					<h2>보고 싶은 곳을 직접 묶어도 좋아요.</h2>
				</div>
				<a href={"/airports/" + airport.toLowerCase()}>
					공항 이동·숙소·근교 보기 →
				</a>
			</div>
			{!places.length && (
				<p className="form-trust">
					고른 조건에 맞는 시설 정보가 부족해요. 아래는 전체 후보이며 실내·보행
					조건을 확인한 결과가 아니에요.
				</p>
			)}
			<div className="walk-candidate-list">
				{shown.map((place) => (
					<article key={place.placeId}>
						<label>
							<input
								type="checkbox"
								checked={selected.includes(place.placeId)}
								onChange={() =>
									setSelected((ids) =>
										ids.includes(place.placeId)
											? ids.filter((id) => id !== place.placeId)
											: [...ids, place.placeId],
									)
								}
							/>
							{place.title}
						</label>
						<span className="eyebrow">
							{place.area} ·{" "}
							{place.role === "meal"
								? "한 끼"
								: place.role === "rest"
									? "쉬어 갈 곳"
									: "방문 후보"}
						</span>
						<p>{place.selectionReason}</p>
						<details>
							<summary>방문 조건과 공식 안내</summary>
							<p>{place.caution}</p>
							<ExternalLink href={place.sourceUrl}>
								{place.sourceTitle}
							</ExternalLink>
							<ExternalLink href={googleMapsSearchUrl(place.mapQuery)}>
								위치·이동 확인
							</ExternalLink>
						</details>
					</article>
				))}
			</div>
			{selected.length > 0 && (
				<div className="walk-save-shortcut" role="status">
					<strong>고른 곳 {selected.length}개</strong>
					<a
						className="button primary"
						href={
							"/trips/new?destination=" +
							airport +
							"&places=" +
							encodeURIComponent(selected.join(","))
						}
					>
						새 여행 첫날에 담기 →
					</a>
				</div>
			)}
		</section>
	);
}

function SaveWalkToTrip({
	id,
	walk,
	places,
	onSaved,
}: {
	id: string;
	walk: CuratedWalk;
	places: PlaceCandidate[];
	onSaved: (notice: string) => void;
}) {
	const { state, persist, saveError, saving, retry } = useTripWorkspace(id);
	const [date, setDate] = useState("");
	if (state.status === "loading") return <p role="status">여행을 읽는 중…</p>;
	if (state.status !== "ready")
		return (
			<div role="alert">
				<p>여행을 읽지 못했어요. 기존 여행을 덮지 않았어요.</p>
				<button className="button secondary" type="button" onClick={retry}>
					다시 읽기
				</button>
			</div>
		);
	const trip = state.trip;
	const targetDate = trip.days.some((day) => day.date === date)
		? date
		: trip.startDate;
	return (
		<div className="walk-save-target">
			<label>
				담을 날짜
				<select
					value={targetDate}
					onChange={(event) => setDate(event.target.value)}
				>
					{trip.days.map((day, index) => (
						<option value={day.date} key={day.date}>
							{index + 1}일차 · {day.date}
						</option>
					))}
				</select>
			</label>
			<button
				className="button primary"
				type="button"
				disabled={saving || !places.length}
				onClick={async () => {
					if (
						await persist((draft) =>
							Object.assign(
								draft,
								appendTripStops(
									draft,
									targetDate,
									places.map((place) => candidateToStop(place, walk.id)),
								),
							),
						)
					) {
						onSaved(
							"선택한 장소를 담았어요. 같은 날 이미 담은 곳은 그대로 두었어요.",
						);
						track("walk_saved", {
							destinationAirport: trip.destination,
							surface: "existing_trip",
						});
					}
				}}
			>
				{saving ? "담는 중…" : `선택한 ${places.length}곳 담기`}
			</button>
			<a href={`/trips/${id}#itinerary`}>저장한 일정 열기 →</a>
			{saveError && <p role="alert">{saveError}</p>}
		</div>
	);
}

export function WalkDetailPage({ slug }: { slug: string }) {
	const walk = getWalk(slug);
	const [selected, setSelected] = useState<string[]>([]);
	const [initialized, setInitialized] = useState(false);
	const [trips, setTrips] = useState<TripWorkspace[]>([]);
	const [tripId, setTripId] = useState("");
	const [notice, setNotice] = useState("");
	const [listFailed, setListFailed] = useState(false);
	const [short, setShort] = useState(false);
	const condition = new URLSearchParams(window.location.search).get(
		"condition",
	);
	usePageMeta(
		walk
			? `${walk.title} — 동네의 반나절 · 일본골목`
			: "동네를 찾지 못했어요 — 일본골목",
		walk?.summary ?? "다른 동네의 반나절을 골라 주세요.",
	);
	useEffect(() => {
		listTrips()
			.then(setTrips)
			.catch(() => setListFailed(true));
	}, []);
	useEffect(() => {
		if (walk) {
			setSelected(
				[
					...walk.stops,
					...walk.mealOptions.slice(0, 1),
					...walk.restOptions.slice(0, 1),
				]
					.filter(
						(p) =>
							condition !== "rain" || ["indoor", "covered"].includes(p.setting),
					)
					.map((p) => p.placeId),
			);
			setInitialized(true);
			track("walk_detail_open", { surface: "walk_detail" });
		}
	}, [walk?.slug]);
	if (!walk)
		return (
			<Layout>
				<section className="empty-state">
					<h1>이 동네 안내를 찾지 못했어요.</h1>
					<a className="button primary" href="/walks">
						다른 반나절 고르기
					</a>
				</section>
			</Layout>
		);
	const allPlaces = [
		...walk.stops,
		...walk.mealOptions,
		...walk.restOptions,
	].filter((p, i, arr) => arr.findIndex((x) => x.placeId === p.placeId) === i);
	const chosen = allPlaces.filter((place) => selected.includes(place.placeId));
	const matching = trips.filter((trip) =>
		walk.airportCodes.includes(trip.destination as AirportCode),
	);
	const freshness = contentFreshness(walk.checkedAt, new Date().toISOString());
	const toggle = (id: string) =>
		setSelected((values) =>
			values.includes(id)
				? values.filter((value) => value !== id)
				: [...values, id],
		);
	const renderPlaces = (places: PlaceCandidate[], label: string) => (
		<section className="walk-place-group">
			<h2>{label}</h2>
			<ol className="walk-stops">
				{places.map((place) => (
					<li key={place.placeId}>
						<label className="walk-pick">
							<input
								type="checkbox"
								checked={selected.includes(place.placeId)}
								onChange={() => toggle(place.placeId)}
							/>
							<span>{place.title}</span>
						</label>
						{place.japaneseName && <p lang="ja">{place.japaneseName}</p>}
						<p>{place.selectionReason}</p>
						<p className="form-trust">{place.caution}</p>
						<small>
							{place.duration} ·{" "}
							{place.setting === "covered"
								? "지붕 있는 구간 · 연결 구간은 확인"
								: place.setting === "indoor"
									? "실내 · 영업 확인 필요"
									: place.setting === "outdoor"
										? "야외"
										: "실내 여부 미확인"}
						</small>
						<div className="button-row">
							<ExternalLink href={googleMapsSearchUrl(place.mapQuery)}>
								위치와 이동 확인
							</ExternalLink>
							<ExternalLink href={place.sourceUrl}>공식 방문 안내</ExternalLink>
						</div>
					</li>
				))}
			</ol>
		</section>
	);
	return (
		<Layout>
			<article className="walk-detail">
				<header className="walk-detail-head">
					<a className="back-link" href="/walks">
						← 다른 동네와 비교
					</a>
					<span className="eyebrow">{walk.neighborhood}</span>
					<h1>{walk.title}</h1>
					<p>{walk.summary}</p>
					<p className="walk-header-facts">
						산책 {walk.stops.length}곳 · 체류 약 {walk.stayMinutes}분 ·
						식사·이동 별도
					</p>
					<div className="walk-save-shortcut">
						<span>선택 {selected.length}곳 · 날짜는 다음에 정해요</span>
						<a className="button primary" href="#save-walk">
							내 여행에 담기 →
						</a>
					</div>
					<WalkBookmark slug={walk.slug} title={walk.title} />
					<WalkPhoto walk={walk} />
				</header>
				<div className="section walk-detail-body">
					<aside className="walk-practical">
						<h2>이 반나절이 맞을까요?</h2>
						<dl className="walk-fit">
							<div>
								<dt>이런 분께</dt>
								<dd>{walk.fit}</dd>
							</div>
							<div>
								<dt>다른 선택이 나을 때</dt>
								<dd>{walk.notFit}</dd>
							</div>
							<div>
								<dt>시작</dt>
								<dd>{walk.arrivalNote}</dd>
							</div>
							<div>
								<dt>마칠 때</dt>
								<dd>{walk.returnNote}</dd>
							</div>
						</dl>
						<p>{walk.movementNote}</p>
						<p className="walk-caution">{walk.caution}</p>
						<p>
							{freshness === "stale"
								? "자료가 오래되어 다시 확인해야 해요."
								: "당일 영업·혼잡·교통은 별도 확인이 필요해요."}{" "}
							자료 검토 {walk.checkedAt?.slice(0, 10)}
						</p>
						<ExternalLink href={walk.sourceUrl}>
							{walk.sourceTitle}
						</ExternalLink>
						<a href={`/correct?source=${encodeURIComponent(walk.id)}`}>
							달라진 내용 알려주기 →
						</a>
					</aside>
					<div className="walk-route" id="walk-route">
						<div className="walk-budget">
							<span className="eyebrow">내가 고른 반나절</span>
							<h2>꼭 보고 싶은 곳부터 남겨요.</h2>
							<div className="button-row">
								<button
									className="button secondary"
									type="button"
									aria-pressed={!short}
									onClick={() => {
										setShort(false);
										setSelected(
											[
												...walk.stops,
												...walk.mealOptions.slice(0, 1),
												...walk.restOptions.slice(0, 1),
											]
												.filter(
													(place) =>
														condition !== "rain" ||
														["indoor", "covered"].includes(place.setting),
												)
												.map((place) => place.placeId),
										);
									}}
								>
									반나절의 기본 흐름
								</button>
								<button
									className="button secondary"
									type="button"
									aria-pressed={short}
									onClick={() => {
										setShort(true);
										let total = 0;
										setSelected(
											walk.stops
												.filter((place) => {
													const minutes = place.stayMinutes ?? 45;
													if (total + minutes > 90) return false;
													total += minutes;
													return true;
												})
												.map((place) => place.placeId),
										);
									}}
								>
									머무는 시간 90분으로 줄이기
								</button>
							</div>
							<p>
								90분은 장소에서 보내는 시간이에요. 이동·대기·식사 시간에 따라 더
								걸릴 수 있어요. 한 곳씩 더하거나 빼도 괜찮아요.
							</p>
						</div>
						{renderPlaces(walk.stops, "동네를 보는 순서")}
						{renderPlaces(walk.mealOptions, "한 끼를 더한다면")}
						{renderPlaces(walk.restOptions, "잠깐 쉬어 간다면")}
						<section className="walk-perspectives">
							<h2>이 골목을 함께 걷고 싶다면</h2>
							<p>
								{isAppsInTossChannel()
									? "코스를 담은 모집문을 만들고, 보낼 사람을 직접 골라 공유해요."
									: "코스를 담은 모집문을 만들고 오픈카톡에서 약속을 정해요."}{" "}
								개인 여행 일정은 가져오지 않아요.
							</p>
							<a
								className="button secondary"
								href={`/companions/new?category=walk&walk=${encodeURIComponent(walk.slug)}`}
							>
								이 코스 함께 걷기
							</a>
						</section>
						{walk.perspectives.length > 0 && (
							<section className="walk-perspectives">
								<h2>같은 동네, 다른 시선</h2>
								{walk.perspectives.map((view) => (
									<p key={view}>
										{
											{
												books:
													"책: 한 권의 주제와 가게의 선반을 천천히 살펴봐요.",
												tableware:
													"그릇: 매일 쓸 크기와 담을 음식을 떠올리며 하나씩 비교해요.",
												architecture:
													"건물: 외관과 쓰임이 바뀐 흔적을 살펴봐요. 사유 공간에는 들어가지 않아요.",
											}[view]
										}
									</p>
								))}
							</section>
						)}
						<section className="walk-save" id="save-walk">
							<h2>선택한 {chosen.length}곳을 내 여행에.</h2>
							<p>
								선택한 장소:{" "}
								{chosen.map((p) => p.title).join(" → ") ||
									"아직 없어요. 위에서 한 곳 이상 골라 주세요."}
							</p>
							<p>
								시간을 정한 장소 약{" "}
								{chosen.reduce((sum, p) => sum + (p.stayMinutes ?? 0), 0)}분
								{chosen.some((p) => p.stayMinutes === null)
									? " + 식사·쉼 등 직접 정할 시간"
									: ""}{" "}
								· 이동·대기는 별도예요.
							</p>
							{matching.length > 0 && (
								<label>
									이 기기의 여행
									<select
										value={tripId}
										onChange={(event) => setTripId(event.target.value)}
									>
										<option value="">담을 여행을 고르세요</option>
										{matching.map((trip) => (
											<option value={trip.id} key={trip.id}>
												{trip.title} · {trip.startDate}
											</option>
										))}
									</select>
								</label>
							)}
							{tripId && (
								<SaveWalkToTrip
									key={tripId}
									id={tripId}
									walk={walk}
									places={chosen}
									onSaved={setNotice}
								/>
							)}
							{listFailed && (
								<p role="alert">
									이 기기의 기존 여행 목록을 읽지 못했어요.{" "}
									<a href="/trips">내 여행에서 다시 읽기</a>
								</p>
							)}
							{initialized && chosen.length > 0 && (
								<a
									className={`button ${tripId ? "secondary" : "primary"}`}
									href={`/trips/new?destination=${walk.airportCodes[0]}&walk=${encodeURIComponent(walk.slug)}&places=${encodeURIComponent(chosen.map((p) => p.placeId).join(","))}`}
								>
									새 여행에 이 코스 담기
								</a>
							)}
							<p className="form-trust">
								날짜·메모·사진은 이 기기에만 남아요. 담은 뒤 비·피로·시간에 맞춰
								일부를 바꿀 수 있어요.
							</p>
							{notice && <p role="status">{notice}</p>}
						</section>
					</div>
				</div>
			</article>
			<PublicGuideAd surface="walk_end" />
		</Layout>
	);
}
